import { useMemo, useState } from "react";
import { useEntity } from "../hooks/useEntity";
import { scratchPadEntityConfig } from "../entities/ScratchPad";
import {
  Plus,
  Trash2,
  FileText,
  Copy,
  Sparkles,
  PenSquare,
  History,
  ChevronUp,
  ChevronDown,
  Tag,
  X,
} from "lucide-react";

interface ScratchPadPanelProps {
  documentId: number;
  userId: number;
  onInsertText: (text: string) => void;
  entries?: ScratchPadEntry[];
  onCreateEntry?: (entry: ScratchPadEntry) => void;
  onRemoveEntry?: (id: number) => void;
  onReorderEntry?: (entryId: number, targetId: number) => void;
  onUpdateEntry?: (entry: ScratchPadEntry) => void;
}

type ScratchPadEntry = {
  id: number;
  documentId: number;
  userId: number;
  title: string;
  content: string;
  tags: string;
  position?: number;
  created_at: string;
  updated_at: string;
};

type ScratchPadVersion = {
  id: string;
  label: string;
  title: string;
  content: string;
  savedAt: string;
  documentId: number;
  userId: number;
  sourceEntryId: number;
};

export default function ScratchPadPanel({ documentId, userId, onInsertText, entries, onCreateEntry, onRemoveEntry, onReorderEntry, onUpdateEntry }: ScratchPadPanelProps) {
  const { items: entityEntries, create, remove, update } = useEntity<ScratchPadEntry>(scratchPadEntityConfig);
  const usingControlled = Array.isArray(entries) && !!onCreateEntry && !!onRemoveEntry;
  const [showForm, setShowForm] = useState(false);
  const [newEntry, setNewEntry] = useState({ title: "", content: "" });
  const [reorderingEntryId, setReorderingEntryId] = useState<number | null>(null);
  const [developmentState, setDevelopmentState] = useState({
    entry: null as ScratchPadEntry | null,
    title: "",
    content: "",
    versionLabel: "",
  });
  const [activeVersionsEntryId, setActiveVersionsEntryId] = useState<number | null>(null);
  const [versionStore, setVersionStore] = useState<Record<number, ScratchPadVersion[]>>({});
  const [deletedSnapshots, setDeletedSnapshots] = useState<ScratchPadVersion[]>([]);
  const [deletePrompt, setDeletePrompt] = useState<{ entry: ScratchPadEntry | null; label: string }>({ entry: null, label: "" });

  const allEntries = usingControlled ? (entries as ScratchPadEntry[]) : entityEntries;
  const documentEntries = allEntries.filter(e => e.documentId === documentId);
  const documentDeletedSnapshots = deletedSnapshots.filter((snap) => snap.documentId === documentId);

  const parseTags = (entry: ScratchPadEntry) => {
    if (!entry.tags) return [] as string[];
    try {
      const parsed = JSON.parse(entry.tags);
      return Array.isArray(parsed) ? parsed.map((tag) => String(tag)) : [];
    } catch {
      return [];
    }
  };

  const getOrderValue = (entry: ScratchPadEntry) => {
    if (typeof entry.position === "number") return entry.position;
    const fallback = Date.parse(entry.created_at);
    return Number.isNaN(fallback) ? Date.now() : fallback;
  };

  const isTopicSuggestion = (entry: ScratchPadEntry) => parseTags(entry).includes("topic-assistant");

  const { topicEntries, generalEntries } = useMemo(() => {
    const ordered = [...documentEntries].sort((a, b) => getOrderValue(a) - getOrderValue(b));
    return {
      topicEntries: ordered.filter(isTopicSuggestion),
      generalEntries: ordered.filter((entry) => !isTopicSuggestion(entry)),
    };
  }, [documentEntries]);

  const generateId = () => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return `ver-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  };

  const versionLabelForEntry = (entryId: number) => `Version ${(versionStore[entryId]?.length ?? 0) + 1}`;

  const addVersionSnapshot = (entry: ScratchPadEntry, label?: string) => {
    const snapshot: ScratchPadVersion = {
      id: generateId(),
      label: label?.trim() || versionLabelForEntry(entry.id),
      title: entry.title || "Untitled snippet",
      content: entry.content,
      savedAt: new Date().toISOString(),
      documentId: entry.documentId,
      userId: entry.userId,
      sourceEntryId: entry.id,
    };
    setVersionStore((prev) => ({
      ...prev,
      [entry.id]: [snapshot, ...(prev[entry.id] ?? [])],
    }));
    return snapshot;
  };

  const persistEntryUpdate = async (entry: ScratchPadEntry, updates: Partial<ScratchPadEntry>) => {
    if (usingControlled && onUpdateEntry) {
      onUpdateEntry({ ...entry, ...updates, updated_at: new Date().toISOString() });
      return;
    }
    await update(entry.id, updates);
  };

  const openDevelopment = (entry: ScratchPadEntry) => {
    setDevelopmentState({
      entry,
      title: entry.title || "",
      content: entry.content,
      versionLabel: versionLabelForEntry(entry.id),
    });
  };

  const closeDevelopment = () => {
    setDevelopmentState({ entry: null, title: "", content: "", versionLabel: "" });
  };

  const handleDevelopmentSave = async () => {
    if (!developmentState.entry) return;
    const entry = developmentState.entry;
    addVersionSnapshot(entry, developmentState.versionLabel);
    await persistEntryUpdate(entry, {
      title: developmentState.title || entry.title,
      content: developmentState.content,
    });
    closeDevelopment();
  };

  const toggleVersionPanel = (entry: ScratchPadEntry) => {
    setActiveVersionsEntryId((prev) => (prev === entry.id ? null : entry.id));
  };

  const handleRestoreVersion = async (entry: ScratchPadEntry, version: ScratchPadVersion) => {
    addVersionSnapshot(entry, versionLabelForEntry(entry.id));
    await persistEntryUpdate(entry, {
      title: version.title,
      content: version.content,
    });
  };

  const handleRenameVersion = (entryId: number, versionId: string, label: string) => {
    setVersionStore((prev) => ({
      ...prev,
      [entryId]: (prev[entryId] ?? []).map((ver) =>
        ver.id === versionId ? { ...ver, label } : ver
      ),
    }));
  };

  const confirmDeleteEntry = (entry: ScratchPadEntry) => {
    setDeletePrompt({ entry, label: `${entry.title || "Snippet"} (deleted)` });
  };

  const handleArchiveEntry = async () => {
    if (!deletePrompt.entry) return;
    const entry = deletePrompt.entry;
    const snapshot = addVersionSnapshot(entry, deletePrompt.label);
    setDeletedSnapshots((prev) => [{ ...snapshot }, ...prev]);
    if (usingControlled) {
      onRemoveEntry?.(entry.id);
    } else {
      await remove(entry.id);
    }
    setDeletePrompt({ entry: null, label: "" });
  };

  const handleRestoreDeleted = async (snapshot: ScratchPadVersion) => {
    if (usingControlled && onCreateEntry) {
      onCreateEntry({
        id: Date.now(),
        documentId: snapshot.documentId,
        userId: snapshot.userId,
        title: snapshot.title,
        content: snapshot.content,
        tags: "[]",
        position: Date.now(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
    } else {
      await create({
        documentId: snapshot.documentId,
        userId: snapshot.userId,
        title: snapshot.title,
        content: snapshot.content,
        tags: "[]",
        position: Date.now(),
      });
    }
    setDeletedSnapshots((prev) => prev.filter((ver) => ver.id !== snapshot.id));
  };

  const handleRenameDeleted = (snapshotId: string, label: string) => {
    setDeletedSnapshots((prev) => prev.map((ver) => (ver.id === snapshotId ? { ...ver, label } : ver)));
  };

  const handleRemoveDeletedSnapshot = (snapshotId: string) => {
    setDeletedSnapshots((prev) => prev.filter((ver) => ver.id !== snapshotId));
  };

  const handleSave = async () => {
    if (!newEntry.content) return;

    if (usingControlled) {
      const entry: ScratchPadEntry = {
        id: Date.now(),
        documentId,
        userId,
        title: newEntry.title || "Untitled",
        content: newEntry.content,
        tags: "[]",
        position: Date.now(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      onCreateEntry?.(entry);
    } else {
      await create({
        documentId,
        userId,
        title: newEntry.title || "Untitled",
        content: newEntry.content,
        tags: "[]",
        position: Date.now(),
      });
    }

    setNewEntry({ title: "", content: "" });
    setShowForm(false);
  };

  const handleCopyToClipboard = async (content: string) => {
    try {
      await navigator.clipboard.writeText(content);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleMoveEntry = async (entry: ScratchPadEntry, direction: "up" | "down") => {
    const collection = isTopicSuggestion(entry) ? topicEntries : generalEntries;
    const currentIndex = collection.findIndex((item) => item.id === entry.id);
    if (currentIndex === -1) return;
    const targetIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= collection.length) return;
    const targetEntry = collection[targetIndex];

    if (usingControlled) {
      onReorderEntry?.(entry.id, targetEntry.id);
      return;
    }

    setReorderingEntryId(entry.id);
    try {
      const currentPos = getOrderValue(entry);
      const targetPos = getOrderValue(targetEntry);
      await Promise.all([
        update(entry.id, { position: targetPos }),
        update(targetEntry.id, { position: currentPos }),
      ]);
    } catch (err) {
      console.error("Failed to reorder scratch pad entries", err);
    } finally {
      setReorderingEntryId(null);
    }
  };

  const renderEntryCard = (entry: ScratchPadEntry, index: number, total: number) => {
    const isTopic = isTopicSuggestion(entry);
    const canMoveUp = index > 0;
    const canMoveDown = index < total - 1;
    const isBusy = reorderingEntryId === entry.id;
    const isDeveloping = developmentState.entry?.id === entry.id;
    const versions = versionStore[entry.id] ?? [];
    const versionsOpen = activeVersionsEntryId === entry.id;

    return (
      <div
        key={entry.id}
        className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg p-3 shadow-lg hover:bg-white/15 transition-all duration-200"
      >
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex-1">
            <h4 className="text-sm font-medium text-white flex items-center gap-2">
              {isTopic && <Sparkles className="w-3.5 h-3.5 text-amber-300" />}
              {entry.title || "Untitled snippet"}
            </h4>
            <p className="text-xs text-white/70 line-clamp-3 mt-1">{entry.content}</p>
          </div>
          <div className="flex flex-col gap-1">
            <button
              onClick={() => handleMoveEntry(entry, "up")}
              disabled={!canMoveUp || isBusy}
              className="p-1 hover:bg-white/10 rounded-lg text-white/70 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
              title="Move up"
            >
              <ChevronUp className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => handleMoveEntry(entry, "down")}
              disabled={!canMoveDown || isBusy}
              className="p-1 hover:bg-white/10 rounded-lg text-white/70 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
              title="Move down"
            >
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 text-[11px] text-white/50">
            <Tag className="w-3 h-3" />
            {isTopic ? "Topic suggestion" : "Note"}
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => handleCopyToClipboard(entry.content)}
              className="p-1.5 hover:bg-white/10 rounded-lg text-white/70 hover:text-white transition-all duration-200"
              title="Copy to clipboard"
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => onInsertText(entry.content)}
              className="px-2 py-1 text-xs bg-white/10 hover:bg-white/20 border border-white/20 rounded text-white transition-all duration-200"
            >
              Insert
            </button>
            <button
              onClick={() => {
                confirmDeleteEntry(entry);
              }}
              className="p-1.5 hover:bg-white/10 rounded-lg text-white/70 hover:text-white transition-all duration-200"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            onClick={() => openDevelopment(entry)}
            className="px-2.5 py-1 rounded-full border border-emerald-400/40 text-[11px] text-emerald-200 flex items-center gap-1 hover:bg-emerald-500/10"
          >
            <PenSquare className="w-3 h-3" /> Develop
          </button>
          <button
            onClick={() => toggleVersionPanel(entry)}
            className="px-2.5 py-1 rounded-full border border-sky-400/40 text-[11px] text-sky-200 flex items-center gap-1 hover:bg-sky-500/10"
          >
            <History className="w-3 h-3" /> Versions ({versions.length})
          </button>
        </div>
        {isDeveloping && (
          <div className="mt-3 bg-white/5 border border-white/15 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between text-xs text-white/70">
              <span>Develop idea</span>
              <button
                type="button"
                onClick={closeDevelopment}
                className="text-white/60 hover:text-white"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <input
              value={developmentState.title}
              onChange={(e) => setDevelopmentState((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="Development topic name"
              className="w-full px-3 py-2 rounded border border-white/20 bg-white/5 text-sm text-white placeholder-white/50"
            />
            <textarea
              value={developmentState.content}
              onChange={(e) => setDevelopmentState((prev) => ({ ...prev, content: e.target.value }))}
              className="w-full min-h-[100px] px-3 py-2 rounded border border-white/20 bg-white/5 text-sm text-white placeholder-white/50"
              placeholder="Expand on this idea..."
            />
            <div>
              <label className="text-[11px] text-white/60">Label previous version</label>
              <input
                value={developmentState.versionLabel}
                onChange={(e) => setDevelopmentState((prev) => ({ ...prev, versionLabel: e.target.value }))}
                className="mt-1 w-full px-3 py-2 rounded border border-white/20 bg-white/5 text-xs text-white placeholder-white/40"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleDevelopmentSave}
                className="flex-1 px-3 py-2 rounded bg-emerald-500/20 text-emerald-100 border border-emerald-400/40 text-sm"
              >
                Save development
              </button>
              <button
                onClick={closeDevelopment}
                className="px-3 py-2 rounded bg-white/10 text-white/70 border border-white/20 text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        {versionsOpen && (
          <div className="mt-3 bg-white/5 border border-white/15 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between text-xs text-white/70">
              <span>Deleted & previous versions</span>
              <button
                type="button"
                onClick={() => setActiveVersionsEntryId(null)}
                className="text-white/60 hover:text-white"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            {versions.length === 0 && (
              <p className="text-[11px] text-white/40">No versions saved yet.</p>
            )}
            {versions.map((version) => (
              <div key={version.id} className="border border-white/10 rounded-lg p-2 space-y-2 bg-black/10">
                <input
                  value={version.label}
                  onChange={(e) => handleRenameVersion(entry.id, version.id, e.target.value)}
                  className="w-full text-xs px-2 py-1 rounded bg-white/5 border border-white/15 text-white"
                />
                <p className="text-[11px] text-white/60 line-clamp-3">{version.content}</p>
                <div className="flex items-center justify-between text-[10px] text-white/40">
                  <span>{new Date(version.savedAt).toLocaleString()}</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleRestoreVersion(entry, version)}
                      className="px-2 py-1 rounded bg-emerald-500/20 text-emerald-100 border border-emerald-400/40"
                    >
                      Restore
                    </button>
                    <button
                      onClick={() =>
                        setVersionStore((prev) => ({
                          ...prev,
                          [entry.id]: (prev[entry.id] ?? []).filter((v) => v.id !== version.id),
                        }))
                      }
                      className="px-2 py-1 rounded bg-white/10 text-white/70 border border-white/20"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const sections = [
    {
      id: "topic",
      label: "Topic Suggestions",
      entries: topicEntries,
      emptyText: "No saved suggestions yet",
    },
    {
      id: "general",
      label: "General Notes",
      entries: generalEntries,
      emptyText: "No saved notes yet",
    },
  ];

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-white/90" />
          <h3 className="text-lg font-semibold text-white">Scratch Pad</h3>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="p-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg text-white transition-all duration-200"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {showForm && (
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-4 mb-4 shadow-lg">
          <input
            type="text"
            placeholder="Title (optional)"
            value={newEntry.title}
            onChange={(e) => setNewEntry({ ...newEntry, title: e.target.value })}
            className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/50 mb-2 focus:outline-none focus:border-white/40"
          />

          <textarea
            placeholder="Save text snippet..."
            value={newEntry.content}
            onChange={(e) => setNewEntry({ ...newEntry, content: e.target.value })}
            className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/50 mb-3 focus:outline-none focus:border-white/40 min-h-[100px] resize-none"
          />

          <button
            onClick={handleSave}
            className="w-full px-4 py-2 bg-white/15 hover:bg-white/25 border border-white/30 rounded-lg text-white font-medium transition-all duration-200"
          >
            Save Snippet
          </button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto space-y-4">
        {sections.map((section) => (
          <div key={section.id}>
            <div className="flex items-center justify-between text-xs text-white/60 mb-2">
              <div className="flex items-center gap-2">
                {section.id === "topic" ? <Sparkles className="w-3.5 h-3.5 text-amber-300" /> : <FileText className="w-3.5 h-3.5 text-white/60" />}
                <span className="uppercase tracking-wide">{section.label}</span>
              </div>
              <span>{section.entries.length}</span>
            </div>
            {section.entries.length ? (
              <div className="space-y-3">
                {section.entries.map((entry, idx) => renderEntryCard(entry, idx, section.entries.length))}
              </div>
            ) : (
              <div className="text-center py-4 text-white/40 text-xs border border-dashed border-white/10 rounded-lg">
                {section.emptyText}
              </div>
            )}
          </div>
        ))}

        {documentDeletedSnapshots.length > 0 && (
          <div className="border border-red-500/30 rounded-xl p-4 space-y-2 bg-red-500/5">
            <div className="flex items-center justify-between text-xs text-red-200">
              <span>Deleted versions</span>
              <span>{documentDeletedSnapshots.length}</span>
            </div>
            {documentDeletedSnapshots.map((snapshot, index) => (
              <div key={snapshot.id} className="bg-black/20 rounded-lg p-3 space-y-2 border border-white/10">
                <div className="flex items-center gap-2 text-[11px] text-white/60">
                  <span>Version {index + 1}</span>
                  <span>•</span>
                  <span>{new Date(snapshot.savedAt).toLocaleString()}</span>
                </div>
                <input
                  value={snapshot.label}
                  onChange={(e) => handleRenameDeleted(snapshot.id, e.target.value)}
                  className="w-full text-xs px-2 py-1 rounded bg-white/5 border border-white/15 text-white"
                />
                <p className="text-[11px] text-white/60 line-clamp-3">{snapshot.content}</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleRestoreDeleted(snapshot)}
                    className="flex-1 px-3 py-1 rounded bg-emerald-500/20 text-emerald-100 border border-emerald-400/40 text-xs"
                  >
                    Restore to scratch pad
                  </button>
                  <button
                    onClick={() => handleRemoveDeletedSnapshot(snapshot.id)}
                    className="px-3 py-1 rounded bg-white/10 text-white/70 border border-white/20 text-xs"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {documentEntries.length === 0 && !showForm && (
          <div className="text-center py-8 text-white/50">
            <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No saved snippets</p>
            <p className="text-xs mt-1">Click + to save text for later</p>
          </div>
        )}
      </div>
      {deletePrompt.entry && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-slate-900 border border-white/10 rounded-xl p-4 w-[320px] space-y-3">
            <h4 className="text-sm text-white font-semibold">Archive snippet</h4>
            <p className="text-xs text-white/60">
              This removes the snippet from the board but keeps a version you can restore later. Name the archived version below.
            </p>
            <input
              value={deletePrompt.label}
              onChange={(e) => setDeletePrompt((prev) => ({ ...prev, label: e.target.value }))}
              className="w-full px-3 py-2 rounded border border-white/20 bg-white/5 text-sm text-white placeholder-white/40"
              placeholder="Deleted version name"
            />
            <div className="flex gap-2">
              <button
                onClick={() => setDeletePrompt({ entry: null, label: "" })}
                className="flex-1 px-3 py-2 rounded border border-white/20 text-white/70"
              >
                Cancel
              </button>
              <button
                onClick={handleArchiveEntry}
                className="flex-1 px-3 py-2 rounded bg-red-500/30 text-red-100 border border-red-400/40"
              >
                Archive
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
